import { useEffect, useState, useRef } from 'react'
import { AppShell } from '../components/layout/AppShell'
import { orgService } from '../services/orgService'
import { isoToLocalInput, localInputToISO, formatTimePT } from '../utils/formatTime'
import { RaceMultiSelect } from '../components/RaceMultiSelect'

type VanStatus = 'online' | 'offline' | 'unknown' | 'n/a'
type SortField = 'bib' | 'name' | 'startTime' | 'projectedFinish' | 'actualFinish'
type SortDir = 'asc' | 'desc'

interface VanInfo {
  status: VanStatus
  lastSeen: string | null
  nearestXP: number | null
  nearestXPMiles: number | null
}

interface TeamRow {
  teamId: number
  teamName: string
  bibNumber: number | null
  division: string | null
  category: string | null
  startTime: string | null
  projectedFinish: string | null
  actualFinishTime: string | null
  van1: VanInfo
  van2: VanInfo
  raceId: number
  raceName: string
  timezone: string
}

const STATUS_CONFIG: Record<Exclude<VanStatus, 'n/a'>, { label: string; dot: string; row: string; text: string }> = {
  online:  { label: 'Online',  dot: 'bg-green-500',  row: '',             text: 'text-green-700' },
  offline: { label: 'Offline', dot: 'bg-yellow-400', row: 'bg-yellow-50', text: 'text-yellow-700' },
  unknown: { label: 'Unknown', dot: 'bg-gray-300',   row: '',             text: 'text-gray-400' },
}

function VanCell({ van, tz }: { van: VanInfo; tz: string }) {
  if (van.status === 'n/a') {
    return <span className="text-gray-300 text-xs">—</span>
  }
  const cfg = STATUS_CONFIG[van.status]
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <span className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</span>
      </div>
      {van.lastSeen && (
        <p className="text-xs text-gray-400 leading-tight">
          {formatTimePT(new Date(van.lastSeen), tz)}
        </p>
      )}
      {van.nearestXP !== null && van.nearestXPMiles !== null && (
        <p className="text-xs text-gray-500 leading-tight">
          XP {van.nearestXP} · {van.nearestXPMiles} mi
        </p>
      )}
    </div>
  )
}

// ── Finish time cell ──────────────────────────────────────────────────────────

function FinishCell({
  team,
  tz,
  onSaved,
}: {
  team: TeamRow
  tz: string
  onSaved: (teamId: number, iso: string | null) => void
}) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [inputVal, setInputVal] = useState('')

  const openEdit = () => {
    setInputVal(team.actualFinishTime ? isoToLocalInput(team.actualFinishTime, tz) : '')
    setEditing(true)
  }

  const save = async (iso: string | null) => {
    setSaving(true)
    try {
      await orgService.recordFinishTime(team.teamId, iso)
      onSaved(team.teamId, iso)
      setEditing(false)
    } catch { /* silent */ }
    finally { setSaving(false) }
  }

  const recordNow = async () => {
    await save(new Date().toISOString())
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1 min-w-[140px]">
        <input
          type="datetime-local"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          className="px-1.5 py-0.5 text-xs border border-indigo-400 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 w-full"
          autoFocus
        />
        <div className="flex gap-1">
          <button
            onClick={() => { if (inputVal) save(localInputToISO(inputVal, tz)) }}
            disabled={!inputVal || saving}
            className="flex-1 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-50"
          >{saving ? '…' : 'Save'}</button>
          {team.actualFinishTime && (
            <button
              onClick={() => save(null)}
              disabled={saving}
              className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200"
            >Clear</button>
          )}
          <button
            onClick={() => setEditing(false)}
            className="px-2 py-0.5 border border-gray-300 text-gray-500 text-xs rounded hover:bg-gray-50"
          >✕</button>
        </div>
      </div>
    )
  }

  if (team.actualFinishTime) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-green-700 font-mono font-semibold text-xs">{new Date(team.actualFinishTime).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true })}</span>
        <button onClick={openEdit} className="text-[10px] text-gray-400 hover:text-indigo-600 text-left">edit</button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        onClick={recordNow}
        disabled={saving}
        className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 font-semibold disabled:opacity-50 whitespace-nowrap"
      >
        {saving ? '…' : '⏱ Record'}
      </button>
      <button onClick={openEdit} className="text-[10px] text-gray-400 hover:text-indigo-600 text-left">enter time</button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function OrgRaceLogPage() {
  const [races, setRaces] = useState<Array<{ id: number; name: string; timezone: string }>>([])
  const [selectedRaceIds, setSelectedRaceIds] = useState<Set<number>>(new Set())
  const [teams, setTeams] = useState<TeamRow[]>([])
  const [loading, setLoading] = useState(true)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [filter, setFilter] = useState<'all' | 'offline' | 'unknown' | 'finished' | 'unfinished'>('all')
  const [divisionFilter, setDivisionFilter] = useState<string>('all')
  const [raceFilter, setRaceFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('startTime')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const scrollRef = useRef<HTMLDivElement>(null)

  const multiRace = selectedRaceIds.size > 1

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir('asc') }
  }

  useEffect(() => {
    orgService.getRaces()
      .then(all => {
        const raceList = all.map(r => ({ id: r.id, name: r.name, timezone: r.timezone ?? 'America/Los_Angeles' }))
        setRaces(raceList)
        const saved = localStorage.getItem('org_selected_race_ids')
        let ids: Set<number>
        if (saved) {
          try {
            const parsed: number[] = JSON.parse(saved)
            const valid = parsed.filter(id => all.some(r => r.id === id))
            ids = valid.length > 0 ? new Set(valid) : new Set(all.map(r => r.id))
          } catch {
            ids = new Set(all.map(r => r.id))
          }
        } else {
          const legacy = localStorage.getItem('org_selected_race_id')
          const legacyId = legacy ? parseInt(legacy, 10) : null
          const match = legacyId ? all.find(r => r.id === legacyId) : null
          ids = match ? new Set([match.id]) : new Set(all.map(r => r.id))
        }
        setSelectedRaceIds(ids)
        if (ids.size === 0) setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const loadAll = async (ids: Set<number>, raceList: Array<{ id: number; name: string; timezone: string }>) => {
    try {
      const results = await Promise.all(
        Array.from(ids).map(async id => {
          const data = await orgService.getRaceLog(id)
          const r = raceList.find(r => r.id === id)
          const name = r?.name ?? ''
          const timezone = r?.timezone ?? 'America/Los_Angeles'
          return (data as TeamRow[]).map(t => ({ ...t, raceId: id, raceName: name, timezone }))
        })
      )
      setTeams(results.flat())
      setLastRefresh(new Date())
    } catch {
      // silent — keep showing last data
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedRaceIds.size === 0 || races.length === 0) return
    loadAll(selectedRaceIds, races)
    const interval = setInterval(() => loadAll(selectedRaceIds, races), 30000)
    return () => clearInterval(interval)
  }, [selectedRaceIds, races])

  const handleRaceSelectionChange = (ids: Set<number>) => {
    setSelectedRaceIds(ids)
    localStorage.setItem('org_selected_race_ids', JSON.stringify(Array.from(ids)))
  }

  // Optimistic update — no reload needed after recording finish time
  const handleFinishSaved = (teamId: number, iso: string | null) => {
    setTeams(prev => prev.map(t => t.teamId === teamId ? { ...t, actualFinishTime: iso } : t))
  }

  const divisions = Array.from(new Set(teams.map(t => t.division).filter(Boolean))) as string[]
  const finishedCount = teams.filter(t => t.actualFinishTime).length

  const filtered = teams
    .filter(t => {
      if (search) {
        const q = search.toLowerCase()
        if (!t.teamName.toLowerCase().includes(q) &&
            !String(t.bibNumber ?? '').includes(q) &&
            !(multiRace && t.raceName.toLowerCase().includes(q))) return false
      }
      if (raceFilter !== 'all' && String(t.raceId) !== raceFilter) return false
      if (filter === 'offline') return t.van1.status === 'offline' || t.van2.status === 'offline'
      if (filter === 'unknown') return t.van1.status === 'unknown' || t.van2.status === 'unknown'
      if (filter === 'finished') return !!t.actualFinishTime
      if (filter === 'unfinished') return !t.actualFinishTime
      if (divisionFilter !== 'all' && t.division !== divisionFilter) return false
      return true
    })
    .sort((a, b) => {
      let cmp = 0
      // When multi-race, secondary sort by race name so same-race teams cluster
      if (multiRace && raceFilter === 'all') {
        cmp = a.raceName.localeCompare(b.raceName)
        if (cmp !== 0) return cmp
      }
      if (sortField === 'bib') {
        cmp = (a.bibNumber ?? 9999) - (b.bibNumber ?? 9999)
      } else if (sortField === 'name') {
        cmp = a.teamName.localeCompare(b.teamName)
      } else if (sortField === 'startTime') {
        const ta = a.startTime ? new Date(a.startTime).getTime() : Infinity
        const tb = b.startTime ? new Date(b.startTime).getTime() : Infinity
        cmp = ta - tb
      } else if (sortField === 'projectedFinish') {
        const ta = a.projectedFinish ? new Date(a.projectedFinish).getTime() : Infinity
        const tb = b.projectedFinish ? new Date(b.projectedFinish).getTime() : Infinity
        cmp = ta - tb
      } else {
        const ta = a.actualFinishTime ? new Date(a.actualFinishTime).getTime() : Infinity
        const tb = b.actualFinishTime ? new Date(b.actualFinishTime).getTime() : Infinity
        cmp = ta - tb
      }
      return sortDir === 'asc' ? cmp : -cmp
    })

  const counts = {
    online: teams.filter(t => t.van1.status === 'online' || t.van2.status === 'online').length,
    offline: teams.filter(t => t.van1.status === 'offline' || t.van2.status === 'offline').length,
    unknown: teams.filter(t => t.van1.status === 'unknown' || t.van2.status === 'unknown').length,
  }

  const exportCSV = () => {
    const fmtTime = (iso: string | null, tz: string) =>
      iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }) : ''
    const fmtDateTime = (iso: string | null, tz: string) =>
      iso ? new Date(iso).toLocaleString('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : ''
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`

    const headers = multiRace
      ? ['Race', 'Bib', 'Team', 'Division', 'Category', 'Start Time', 'Proj. Finish', 'Actual Finish']
      : ['Bib', 'Team', 'Division', 'Category', 'Start Time', 'Proj. Finish', 'Actual Finish']

    const rows = filtered.map(t => {
      const tz = t.timezone
      const cols = [
        ...(multiRace ? [esc(t.raceName)] : []),
        t.bibNumber != null ? String(t.bibNumber) : '',
        esc(t.teamName),
        esc(t.division ?? ''),
        esc(t.category ?? ''),
        fmtTime(t.startTime, tz),
        fmtTime(t.projectedFinish, tz),
        fmtDateTime(t.actualFinishTime, tz),
      ]
      return cols.join(',')
    })

    const csv = [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const now = new Date().toISOString().slice(0, 10)
    a.download = `race-log-${now}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function SortTh({ field, label, className }: { field: SortField; label: string; className?: string }) {
    return (
      <th
        className={`px-2 py-2 text-left font-semibold text-gray-500 cursor-pointer select-none hover:text-indigo-600 whitespace-nowrap ${className ?? ''}`}
        onClick={() => toggleSort(field)}
      >
        {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </th>
    )
  }

  // Track last seen race for row group header
  let lastRaceId: number | null = null

  return (
    <AppShell>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Race Log
              </h1>
              <RaceMultiSelect
                races={races}
                selectedIds={selectedRaceIds}
                onChange={handleRaceSelectionChange}
              />
            </div>
            {lastRefresh && (
              <p className="text-xs text-gray-400">
                Updated {lastRefresh.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            )}
          </div>

          {/* Summary chips */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{counts.online} online
            </span>
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />{counts.offline} offline
            </span>
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />{counts.unknown} unknown
            </span>
            {finishedCount > 0 && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{finishedCount} finished
              </span>
            )}
            <span className="ml-auto flex items-center gap-2">
              <span className="text-xs text-gray-400">{teams.length} teams</span>
              <button
                onClick={exportCSV}
                disabled={filtered.length === 0}
                className="text-xs px-2 py-0.5 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >↓ CSV</button>
            </span>
          </div>

          {/* Search + filter */}
          <div className="flex gap-2 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={multiRace ? 'Search team, bib, or race…' : 'Search team or bib…'}
              className="flex-1 min-w-[120px] px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            {multiRace && (
              <select
                value={raceFilter}
                onChange={e => setRaceFilter(e.target.value)}
                className="text-xs border border-gray-300 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="all">All races</option>
                {races.filter(r => selectedRaceIds.has(r.id)).map(r => (
                  <option key={r.id} value={String(r.id)}>{r.name}</option>
                ))}
              </select>
            )}
            {divisions.length > 0 && (
              <select
                value={divisionFilter}
                onChange={e => setDivisionFilter(e.target.value)}
                className="text-xs border border-gray-300 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="all">All divisions</option>
                {divisions.sort().map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            <select
              value={filter}
              onChange={e => setFilter(e.target.value as any)}
              className="text-xs border border-gray-300 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="all">All</option>
              <option value="unfinished">Not finished</option>
              <option value="finished">Finished</option>
              <option value="offline">Offline only</option>
              <option value="unknown">Unknown only</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">Loading race data…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No teams match</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-center font-semibold text-gray-500 w-10 cursor-pointer select-none hover:text-indigo-600" onClick={() => toggleSort('bib')}>
                    # {sortField === 'bib' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                  <SortTh field="name" label="Team" />
                  <SortTh field="startTime" label="Start" className="w-16" />
                  <SortTh field="projectedFinish" label="Proj." className="w-16" />
                  <SortTh field="actualFinish" label="Finish ⏱" className="w-28" />
                  <th className="px-2 py-2 text-left font-semibold text-gray-500 w-24">Van 1</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-500 w-24">Van 2</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(team => {
                  const hasIssue = team.van1.status !== 'online' ||
                    (team.van2.status !== 'online' && team.van2.status !== 'n/a')
                  const isFinished = !!team.actualFinishTime
                  // Inject a race group header row when switching races in multi-race view
                  const showRaceHeader = multiRace && raceFilter === 'all' && team.raceId !== lastRaceId
                  if (showRaceHeader) lastRaceId = team.raceId
                  return (
                    <>
                      {showRaceHeader && (
                        <tr key={`race-header-${team.raceId}`} className="bg-indigo-50">
                          <td colSpan={7} className="px-3 py-1.5">
                            <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wide">{team.raceName}</span>
                          </td>
                        </tr>
                      )}
                      <tr
                        key={`${team.raceId}-${team.teamId}`}
                        className={`border-b border-gray-100 ${
                          isFinished ? 'bg-emerald-50' :
                          hasIssue && team.van1.status === 'offline' ? 'bg-yellow-50' : ''
                        }`}
                      >
                        <td className="px-2 py-2 text-center font-mono text-gray-400">
                          {team.bibNumber ?? '—'}
                        </td>
                        <td className="px-2 py-2">
                          <p className="font-semibold text-gray-900 truncate max-w-[110px]">{team.teamName}</p>
                          {(team.division || team.category) && (
                            <p className="text-[10px] text-gray-400 leading-tight">
                              {[team.division, team.category].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {team.startTime ? new Date(team.startTime).toLocaleTimeString('en-US', { timeZone: team.timezone, hour: 'numeric', minute: '2-digit', hour12: true }) : '—'}
                        </td>
                        <td className="px-2 py-2 text-xs whitespace-nowrap">
                          {team.projectedFinish
                            ? <span className="text-indigo-600 font-mono">{new Date(team.projectedFinish).toLocaleTimeString('en-US', { timeZone: team.timezone, hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-2 py-2">
                          <FinishCell team={team} tz={team.timezone} onSaved={handleFinishSaved} />
                        </td>
                        <td className="px-2 py-2">
                          <VanCell van={team.van1} tz={team.timezone} />
                        </td>
                        <td className="px-2 py-2">
                          <VanCell van={team.van2} tz={team.timezone} />
                        </td>
                      </tr>
                    </>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppShell>
  )
}
